import React, { Dispatch, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FormControl,
    FormHelperText,
    FormLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    SxProps,
    TextField,
} from '@mui/material';
import { headingFontBold, labelFontSemiBold, bodyFont, vars } from '../../../../../Theme';
import { IAppReduxStore } from '../../../../../redux/store';
import { ICreateSupportTicketMainState } from '../CreateSupportTicketDialog';
import { useSelector } from 'react-redux';
import { ISupportTemplate } from '../../../../../redux/project/sliceProjectSupportTicketCreation';
import UploadFile from '../../_parts/UploadFile';
import {
    appendFiles,
    getMappedFilesToRelatedDocuments,
    removeFile,
} from '../../../../../redux/import/sliceImportFileUpload';
import RadioBtnGroup from '../../_parts/RadioBtnGroup';
import InputEmail from '../../_parts/InputEmail';
import Message from '../../_parts/Message';
import { mdiAlertCircle, mdiInformationOutline } from '@mdi/js';
import { supportTicketMaximumFileSize } from '../../../../../Constants';
import AppIcon from '../../../Icons/AppIcon';

/** Styles for FormLabel. */
const formLabelSx: SxProps = {
    ...headingFontBold,
    fontSize: '16px',
    color: vars.black,
    fontWeight: 700,
    marginBottom: '2px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

const textFieldSx: SxProps = {
    input: { ...bodyFont, p: 0 },

    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            border: `2px solid ${vars.black}`,
        },
    },
    '& .MuiFormHelperText-root': {
        marginLeft: 'unset',
    },

    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
        py: '6.5px',
        pl: '12px',
    },
};
const optionSx: SxProps = {
    ...labelFontSemiBold,
    fontSize: '16px',
    color: vars.font,
    fontWeight: 500,
};

const selectSx: SxProps = {
    '& .MuiInputBase-inputSizeSmall': {
        py: '6.5px',
        pl: '12px',
    },
};

const helperText = (text: string) => {
    return (
        <Message
            text={text}
            icon={mdiAlertCircle}
            iconSize={16 / 24}
            color={vars.errorDialogRed}
            typographySx={{
                fontWeight: 400,
                fontSize: '12px',
                ...bodyFont,
            }}
            wrapperSx={{
                border: 'none',
                p: 0,
                m: 0,
                gap: 0.5,
            }}
        />
    );
};

export default ({
    mainState,
    handleChange,
    handleChangeTemplate,
    showErrorState,
    setShowErrorState,
}: ICreateSupportTicketProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'createSupportTicketDialog' });
    const { templates } = useSelector(
        (_store: IAppReduxStore) => _store.projectSupportTicketCreation,
    );
    const documents = useSelector(getMappedFilesToRelatedDocuments);

    const isDisabled = useMemo(() => {
        return !mainState.templateUNID;
    }, [mainState.templateUNID]);

    return (
        <Stack spacing={'12px'}>
            <FormControl size='small' required>
                <FormLabel sx={{ ...formLabelSx, marginTop: 0 }}>{t('template')}</FormLabel>
                <Select
                    size='small'
                    sx={selectSx}
                    value={mainState.templateUNID}
                    error={showErrorState.templateUNID}
                    onChange={(e: SelectChangeEvent<string>) => {
                        handleChangeTemplate(templates.find(t => t.docUNID === e.target.value));
                    }}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                >
                    {(templates ?? []).map(option => (
                        <MenuItem key={option.docUNID} value={option.docUNID} sx={optionSx}>
                            {option.description}
                        </MenuItem>
                    ))}
                </Select>
                {showErrorState.templateUNID && (
                    <FormHelperText sx={{ mx: 0 }}>{helperText(t('errorInput'))}</FormHelperText>
                )}
            </FormControl>
            <FormControl disabled={isDisabled}>
                <FormLabel sx={formLabelSx}>{t('subject')}</FormLabel>
                <TextField
                    sx={textFieldSx}
                    size='small'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange({ field: 'subject', value: e.target.value })
                    }
                    value={mainState.subject}
                    disabled={isDisabled}
                />
            </FormControl>
            <FormControl required disabled={isDisabled}>
                <FormLabel sx={formLabelSx}>{t('description')}</FormLabel>
                <TextField
                    sx={textFieldSx}
                    size='small'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange({ field: 'body', value: e.target.value })
                    }
                    value={mainState.body}
                    error={showErrorState.body}
                    helperText={showErrorState.body && helperText(t('errorInput'))}
                    disabled={isDisabled}
                    multiline
                />
            </FormControl>
            <FormControl>
                <FormLabel sx={formLabelSx}>{t('filesAttachment')}</FormLabel>
                <UploadFile
                    buttonLabel={t('addFiles')}
                    documentsLabel={t('filesOverview', { count: documents.length })}
                    acceptFileTypes='application/pdf,.doc,.docx,application/msword'
                    appendFilesInfo={appendFiles}
                    removeFile={removeFile}
                    uploadedDocuments={documents}
                    allowMultiple={true}
                    maximumFileSize={supportTicketMaximumFileSize}
                    showError={showErrorState.exceededFileSize}
                    setShowErrorState={setShowErrorState}
                />
                {showErrorState.exceededFileSize && (
                    <FormHelperText sx={{ mx: 0 }}>
                        {helperText(t('exceededFileSize'))}
                    </FormHelperText>
                )}
            </FormControl>
            <RadioBtnGroup
                horizontal
                label={t('sendCopyTo')}
                value={String(mainState.sendCopyTo)}
                options={[
                    {
                        key: String(true),
                        label: t('yes'),
                    },
                    {
                        key: String(false),
                        label: t('no'),
                    },
                ]}
                onSelect={key => handleChange({ field: 'sendCopyTo', value: key })}
            />
            <FormControl disabled={isDisabled}>
                <Stack direction='row' spacing={1}>
                    <FormLabel sx={formLabelSx}>{t('recipient')}</FormLabel>
                    <AppIcon path={mdiInformationOutline} tooltipText={t('recipientInfotext')} tooltipPlacement='top-start' />
                </Stack>
                <InputEmail
                    emails={mainState.copyTo}
                    setEmails={handleChange}
                    field='copyTo'
                    placeholder={t('enterRecipients')}
                    hasError={showErrorState.copyTo}
                    errorMessage={t('errorInput')}
                    isDisabled={isDisabled}
                />
            </FormControl>
        </Stack>
    );
};

interface ICreateSupportTicketProps {
    mainState: ICreateSupportTicketMainState;
    handleChange: ({ field, value }: { field: string; value: string | string[] }) => void;
    handleChangeTemplate: (template: ISupportTemplate) => void;
    showErrorState: { [P in keyof ICreateSupportTicketMainState]?: boolean };
    setShowErrorState: Dispatch<
        React.SetStateAction<{ [P in keyof ICreateSupportTicketMainState]?: boolean }>
    >;
}
